import type { TAsyncState, TStateCreator } from "../types";
import {
    initialAsyncState,
    pendingAsyncState,
    fulfilledAsyncState,
    rejectedAsyncState,
} from "../types";

/**
 * Homepage v2 view (matches backend HomepageView)
 */
export type THomepageView = "service_provider" | "accountant";

/**
 * Homepage v2 board column key (matches backend HomepageColumnKey)
 */
export type THomepageColumnKey =
    | "not_yet_due"
    | "upload_now"
    | "submitted"
    | "waiting_on_service_provider"
    | "ready_for_you"
    | "submitted_to_erp";

/**
 * Accountant next action (matches backend NextAction). The row also carries a
 * server-rendered `nextActionLabel`; this identifier is for branching, not
 * display.
 */
export type THomepageNextAction =
    | "processing"
    | "fix_errors"
    | "balance"
    | "commit_otb"
    | "waiting_on_gl"
    | "adjust"
    | "generate_finjournal"
    | "generate_data_platform";

/**
 * One board row (matches backend THomepageRow)
 */
export type THomepageRow = {
    dealId: string;
    dealName: string;
    serviceProviderName: string | null;
    portfolioCompanyName: string | null;
    dealFileType: string;
    fileType: string;
    period: string;
    dueDate: string | null;
    daysUntilDue: number | null;
    uploadAttempts: number;
    failedAttempts: number;
    status: string | null;
    submittedAt: string | null;
    submittedByName: string | null;
    submittedOnTime: boolean | null;
    daysSinceSubmitted: number | null;
    /** The file to open on click — null on a row with nothing uploaded yet. */
    fileId: string | null;
    completedAt: string | null;
    nextAction: THomepageNextAction | null;
    nextActionLabel: string | null;
    errorCount: number | null;
    isClickable: boolean;
};

/**
 * One board column (matches backend THomepageColumn)
 */
export type THomepageColumn = {
    key: THomepageColumnKey;
    label: string;
    count: number;
    totalCount: number;
    rows: THomepageRow[];
};

/**
 * The full board (matches backend THomepageBoard)
 */
export type THomepageBoard = {
    view: THomepageView;
    selectedPeriods: string[];
    availablePeriods: string[];
    serverDate: string;
    columns: THomepageColumn[];
};

/**
 * Homepage board request filters. `periods` empty means "let the backend
 * default to the previous month" — the wire layer (wireHomepage) syncs this
 * back to `board.selectedPeriods` once a response lands, so the checkbox
 * group always reflects the server's actual selection, not a guess.
 */
export type THomepageFilters = {
    view: THomepageView | null; // Admin "Board view" override (ticket 12) — null elsewhere
    periods: string[];
    businessPartnerType: string | null;
    businessPartnerId: string | null;
    /** Display label for `businessPartnerId`, paired with it for the same
     * reason as `dealLabel`. */
    businessPartnerLabel: string | null;
    dealId: string | null;
    /** Display label for `dealId`, kept beside it so the Submission dropdown
     * can render its own selection without re-fetching the deal by id. */
    dealLabel: string | null;
    financeUserId: string | null;
    /** Display label for `financeUserId`, paired with it for the same reason as
     * `dealLabel`. */
    financeUserLabel: string | null;
    /** Rows per column to request. Raised by "Show more"; the server clamps it
     * and still reports each column's true total. */
    rowLimit: number | null;
};

/**
 * Homepage slice state
 */
export type THomepageState = {
    homepageBoardState: TAsyncState<THomepageBoard>;
    homepageFilters: THomepageFilters;
};

/**
 * Homepage slice actions
 */
export type THomepageActions = {
    setHomepageBoardLoading: () => void;
    setHomepageBoard: (data: THomepageBoard) => void;
    setHomepageBoardError: (error: Error) => void;
    setHomepageFilters: (filters: Partial<THomepageFilters>) => void;
    resetHomepageFilters: () => void;
};

/**
 * Complete homepage slice type
 */
export type TSliceHomepage = THomepageState & THomepageActions;

const defaultFilters: THomepageFilters = {
    view: null,
    periods: [],
    businessPartnerType: null,
    businessPartnerId: null,
    businessPartnerLabel: null,
    dealId: null,
    dealLabel: null,
    financeUserId: null,
    financeUserLabel: null,
    rowLimit: null,
};

const initialState: THomepageState = {
    homepageBoardState: initialAsyncState(),
    homepageFilters: defaultFilters,
};

/**
 * Creates the Homepage v2 slice for the store.
 */
export const createSliceHomepage = (): TStateCreator<TSliceHomepage> => {
    return (set) => ({
        ...initialState,

        setHomepageBoardLoading: () => {
            set((state) => ({
                ...state,
                homepageBoardState: pendingAsyncState(state.homepageBoardState.data),
            }));
        },

        setHomepageBoard: (data: THomepageBoard) => {
            set({ homepageBoardState: fulfilledAsyncState(data) });
        },

        setHomepageBoardError: (error: Error) => {
            set((state) => ({
                ...state,
                homepageBoardState: rejectedAsyncState(error, state.homepageBoardState.data),
            }));
        },

        setHomepageFilters: (filters: Partial<THomepageFilters>) => {
            set((state) => ({
                ...state,
                homepageFilters: { ...state.homepageFilters, ...filters },
            }));
        },

        resetHomepageFilters: () => {
            set({ homepageFilters: defaultFilters });
        },
    });
};
